import React, {useCallback, useState, useMemo} from 'react'
import {Col, media, Row} from 'styled-bootstrap-grid'
import {AnimatePresence, motion} from 'framer-motion'
import styled from 'styled-components'

import type {
  AttributeCountableConnection,
  Category,
  ProductCountableConnection,
  ProductCountableEdge,
} from 'src/api/saleor/generated'
import {DatocmsContent} from 'src/components/content/DatocmsContent'
import {useWindowSize} from 'react-use'
import type {Content_categoryLandingPage} from 'src/api/datocms/types/CategoryLandingPage'
import {ProductCard} from 'components/products'
import {palette, theme} from 'styled-tools'
import screenTheme from 'src/themes/theme'
import {theme as Theme} from 'src/themes'
import {SpacingWrapper} from '../layout'
import {H1, PLarge} from '../typography'
import {CategorySidebar} from '.'
import {Loader} from '../common'
import {Button} from '../buttons'
import {Checkbox} from '../form'

interface Props {
  category: Category
  products: ProductCountableConnection
  currentCategoryProductsSlug: string | null | undefined
  filterableAttr: AttributeCountableConnection
  landingPage?: Content_categoryLandingPage[] | null
  sort: Filters | null
  updateSortOpt: (val: Filters | null) => void
  envFilter: boolean
  updateEnvFilter: (val: boolean) => void
  showLoader: boolean
}

type Filters = 'new' | 'price_asc' | 'rating' | 'alph_asc'

const AnimateCol = motion(Col)
const CategoryTitleContainer = styled(H1)`
  position: relative;
`
const CategoryTitle = styled(motion.div)`
  postion: absolute;

  top: 0;
  left: 0;
`

const LoaderContainer = styled.div`
  width: 100%;
  display: grid;
  place-items: center;
`
const LandingPageContainer = styled.div`
  width: 100%;
  .landing_container {
    margin-top: -60px;
    display: flex;
    gap: 30px;
    flex-direction: column;
    justify-content: center;
  }
`
const ShowInMobile = styled.div`
  display: block;
  position: relative;

  ${media.lg`
    display: none;
  `}
`

const MobileSubCatContainer = styled.div<{isOpen: boolean}>`
  position: absolute;
  z-index: ${theme('zIndex.select')};
  top: 48px;
  left: 0;
  width: 100%;
  background-color: ${palette('white')};
  pointer-events: ${({isOpen}) => (isOpen ? 'unset' : 'none')};
  opacity: ${({isOpen}) => (isOpen ? 1 : 0)};
  transition: opacity 0.15s;
`

const RightContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  .message-box {
    margin: 40px;
  }
`
const SortDropdown = styled.div`
position: relative;
width: 220px;
display: flex;
flex-direction: column;
justify-content: center;
align-items: center;
background-color: ${palette('white')};
border-radius: 5px;
border: 1.5px solid ${palette('black')};
  .sort-button {
    background-color: transparent;
    border: none !important;
    outline: none !important;
    width: 100%;
    height: 40px;
    display: flex;
    flex-direction: row;
    align-items: center;
    span {
      font-size: 16px;
      flex-grow: 1;
      display: flex;
      justify-content: flex-start;
    }
  }
}
.options {
  width: 100%;
  position: absolute;
  top: 40px;
  z-index: 10;
  border: 1px solid ${palette('black')};
  background-color: ${palette('white')};
  ${media.lg`
    position: relative;
    top: 0;
    border-top: border-top: 1px solid ${palette('black')};
    border-bottom: none !important;
    border-left: none !important;
    border-right: none;
  `}
  li {
    padding: 5px;
    button {
    width: 100%;
    text-align: left;
    background-color: transparent;
    border: none !important;
    font-size: 16px;
    &:hover {
      color: ${Theme.palette.error};
    }
    .selected {
      button {
        color: ${Theme.palette.error};
      }
    }
    }
    &:hover {
      cursor: pointer;
    }
  }
}
${media.lg`
  position: absolute;
  z-index: 10;
  right: 0;
  top: -60px;
`}
align-self: flex-end;
margin-bottom: 10px;
.arrow {
  border: solid black;
  border-width: 0 3px 3px 0;
  display: inline-block;
  padding: 3px;
}
.up {
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
}
.down {
  transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
}
`

const GetItem = ({
  product,
  index,
}: {
  product: ProductCountableEdge
  index: number
}): JSX.Element | null => {
  if (
    !product.node.id ||
    !product.node.variants ||
    product.node.variants.length === 0
  ) {
    return null
  }
  return (
    <ProductCard
      key={product.node.id}
      slug={product.node.slug}
      render={(children) => (
        <AnimateCol
          key={product.node?.id}
          sm={6}
          md={4}
          transition={{
            duration: 0.3,
            ease: 'anticipate',
            delay: index * 0.1 > 0.2 ? 0.2 : index * 0.1,
          }}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
        >
          {children}
        </AnimateCol>
      )}
    />
  )
}

export default function CategoryPage({
  category,
  products,
  currentCategoryProductsSlug,
  filterableAttr: _filterableAttr,
  landingPage,
  sort,
  updateSortOpt,
  envFilter,
  updateEnvFilter,
  showLoader,
}: Props): JSX.Element {
  const [
    showMobileSubCategoryList,
    setShowMobileSubCategoryList,
  ] = useState<boolean>(false)

  const showProducts = currentCategoryProductsSlug === category?.slug
  const {width} = useWindowSize()
  const isMobile = useMemo(() => {
    const minScreenWidth = screenTheme.screenSize.sizeLG.slice(0, -2)
    return width < Number(minScreenWidth)
  }, [width])

  const isTopLevel = !category?.parent && category?.children?.edges?.length

  const toogleSubCategoryList = useCallback(() => {
    setShowMobileSubCategoryList(!showMobileSubCategoryList)
  }, [showMobileSubCategoryList])

  const SortContainer = (): JSX.Element => {
    const [showDropdown, setShowDropdown] = useState<boolean>(false)

    const handleChange = (filter: Filters | null): void => {
      updateSortOpt(filter)
    }

    const options: Record<Filters, string> = {
      new: 'Nýjast',
      price_asc: 'Lægsta verð',
      alph_asc: 'Nafn',
      rating: 'Rating',
    }

    return (
      <SortDropdown>
        <button
          type="button"
          className="sort-button"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <span>
            {sort ? `Raða eftir: ${options[sort]}` : 'Raða eftir: Nafn'}
          </span>
          <i className={showDropdown ? 'arrow up' : 'arrow down'} />
        </button>
        {showDropdown && (
          <div className="options">
            <ul>
              <li>
                <button
                  type="button"
                  onClick={() => handleChange('new')}
                  className={sort === 'new' ? 'selected' : ''}
                >
                  {options.new}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => handleChange('price_asc')}
                  className={sort === 'price_asc' ? 'selected' : ''}
                >
                  {options.price_asc}
                </button>
              </li>

              <li>
                <button
                  type="button"
                  onClick={() => handleChange('alph_asc')}
                  className={sort === 'alph_asc' ? 'selected' : ''}
                >
                  {options.alph_asc}
                </button>
              </li>
            </ul>
          </div>
        )}
      </SortDropdown>
    )
  }

  return (
    <SpacingWrapper spacing={32}>
      <AnimatePresence initial={false} exitBeforeEnter>
        <CategoryTitleContainer withGutter>
          <CategoryTitle
            key={category?.name || 'Vefverslun'}
            transition={{duration: 0.2, ease: 'anticipate'}}
            initial={{x: 0, opacity: 0}}
            animate={{x: 0, opacity: 1}}
            exit={{x: 300, opacity: 0}}
          >
            {category?.name || 'Vefverslun'}
          </CategoryTitle>
        </CategoryTitleContainer>
      </AnimatePresence>
      <ShowInMobile>
        <Button fill outlined withGutter onClick={toogleSubCategoryList}>
          {!showMobileSubCategoryList ? 'Sýna undirflokka' : 'Fela undirflokka'}
        </Button>
        <MobileSubCatContainer isOpen={showMobileSubCategoryList}>
          <CategorySidebar
            category={category}
            setShowMobileSubCategoryList={setShowMobileSubCategoryList}
          />
        </MobileSubCatContainer>
      </ShowInMobile>

      <Row>
        <Col lg={3} hiddenLgDown>
          <CategorySidebar
            category={category}
            isLandingPage={!!(isTopLevel && landingPage?.length)}
          />
        </Col>
        <Col lg={9}>
          {/* TODO: Hide filter bar while sorting is checked out */}
          {/* <CategoryFilterBar filterableAttr={filterableAttr} /> */}
          <div>
            {landingPage?.length ? (
              <LandingPageContainer>
                <div
                  className={
                    (landingPage[0].__typename === 'BannerRecord' &&
                      !isMobile) ||
                    (landingPage[0].__typename === 'BannerSliderRecord' &&
                      !isMobile)
                      ? 'landing_container'
                      : ''
                  }
                >
                  <DatocmsContent content={landingPage} />
                </div>
              </LandingPageContainer>
            ) : (
              <AnimatePresence initial={false}>
                {showLoader || (showProducts && !products) ? (
                  <LoaderContainer>
                    <Loader loaderKey={category?.slug} size={48} />
                  </LoaderContainer>
                ) : null}
                {!showLoader && showProducts && products && (
                  <RightContainer>
                    <SortContainer />
                    <Checkbox
                      label="Sía vistvænar vörur"
                      checked={envFilter}
                      onClick={() => updateEnvFilter(!envFilter)}
                    />
                    <Row style={{marginTop: '10px'}}>
                      {products?.edges && !products?.edges.length && (
                        <PLarge className="message-box">
                          Engin atriði til að sýna
                        </PLarge>
                      )}
                      {products?.edges?.map((product, index) => (
                        <GetItem
                          product={product}
                          key={product.node.slug ?? index}
                          index={index}
                        />
                      ))}
                    </Row>
                  </RightContainer>
                )}
              </AnimatePresence>
            )}
          </div>
        </Col>
      </Row>
    </SpacingWrapper>
  )
}
